/**
 * Centralised Supabase Realtime channel manager.
 *
 * Why this exists
 * ---------------
 * `supabase.channel(topic)` returns the EXISTING channel when a channel with
 * the same topic is already registered. If that channel has already been
 * subscribed, calling `.on()` on it throws:
 *
 *   "cannot add `postgres_changes` callbacks for realtime:<topic> after `subscribe()`"
 *
 * That happens as soon as two component instances (or a StrictMode double
 * mount) use the same topic. This module removes the whole class of bug:
 *
 *  - Callers never touch `supabase.channel()` directly; they declare their
 *    bindings in a `configure` callback.
 *  - All bindings for a logical topic are applied to a FRESH channel object
 *    (unique physical topic) BEFORE `subscribe()` is ever called.
 *  - Multiple subscribers sharing the same logical topic are ref-counted onto
 *    a single websocket channel; when the set of subscribers changes the
 *    channel is rebuilt from scratch instead of being mutated post-subscribe.
 */
import { supabase } from "@/integrations/supabase/client";
import type { RealtimeChannel } from "@supabase/supabase-js";

export type ChannelConfigure = (channel: RealtimeChannel) => RealtimeChannel | void;

interface Entry {
  /** Logical topic shared by all subscribers of this entry. */
  key: string;
  subscribers: Map<number, ChannelConfigure>;
  channel: RealtimeChannel | null;
  /** Scheduled rebuild, so a burst of mounts results in a single channel. */
  pending: boolean;
  generation: number;
}

const registry = new Map<string, Entry>();
let subscriberSeq = 0;
let topicSeq = 0;

function buildChannel(entry: Entry) {
  const previous = entry.channel;
  entry.channel = null;

  if (entry.subscribers.size === 0) {
    if (previous) void supabase.removeChannel(previous);
    registry.delete(entry.key);
    return;
  }

  // Always a brand-new, never-seen topic => supabase-js can never hand us
  // back an already-subscribed channel, so `.on()` below is always legal.
  entry.generation += 1;
  const physicalTopic = `${entry.key}#${entry.generation}.${++topicSeq}`;
  const channel = supabase.channel(physicalTopic);

  for (const configure of entry.subscribers.values()) {
    try {
      configure(channel);
    } catch (err) {
      console.error("[realtime] channel configure failed", entry.key, err);
    }
  }

  entry.channel = channel;
  channel.subscribe();

  // Remove the old channel only after the new one is wired, to minimise the
  // window where events could be missed.
  if (previous) void supabase.removeChannel(previous);
}

function scheduleRebuild(entry: Entry) {
  if (entry.pending) return;
  entry.pending = true;
  queueMicrotask(() => {
    entry.pending = false;
    buildChannel(entry);
  });
}

/**
 * Register realtime bindings for a logical topic.
 *
 * @param key       Logical topic (e.g. `annotations:<messageId>`). Subscribers
 *                  sharing a key share one websocket channel.
 * @param configure Applies `.on(...)` bindings. Called on a fresh channel and
 *                  may be re-invoked when the channel is rebuilt, so it must
 *                  be side-effect free apart from the bindings.
 * @returns cleanup function; call it on unmount.
 */
export function subscribeChannel(key: string, configure: ChannelConfigure): () => void {
  let entry = registry.get(key);
  if (!entry) {
    entry = { key, subscribers: new Map(), channel: null, pending: false, generation: 0 };
    registry.set(key, entry);
  }

  const id = ++subscriberSeq;
  entry.subscribers.set(id, configure);
  scheduleRebuild(entry);

  let released = false;
  return () => {
    if (released) return;
    released = true;
    const current = registry.get(key);
    if (!current) return;
    current.subscribers.delete(id);
    scheduleRebuild(current);
  };
}

/** Test/diagnostics helper: number of live logical channels. */
export function activeChannelCount(): number {
  return registry.size;
}
